import React, { useState, useEffect } from 'react';
import { X, Save, Euro, Users, MapPin, Tag, Clock, Calendar as CalendarIcon, Trash2, Copy, ShieldAlert, CheckCircle2 } from 'lucide-react';
import { Category, EventItem, FundingModel, StibetType, TaxTier } from '../../core/types.ts';
import { calculateEventFinancials } from '../../core/financials.ts';

interface EventModalProps {
  isOpen: boolean;
  eventToEdit: EventItem | null;
  onClose: () => void;
  onSave: (eventData: EventItem) => void;
  onDelete?: (id: string) => void;
  onDuplicate?: (eventData: EventItem) => void;
}

const CATEGORIES: Category[] = [
  'Culture',
  'Drinking',
  'Sports',
  'Social',
  'Academic',
  'Trip',
  'Art',
  'Other',
];

function formatISO(d: Date): string {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const hh = String(d.getHours()).padStart(2, '0');
  const min = String(d.getMinutes()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}T${hh}:${min}`;
}

function calculateEnd(startISO: string, durationHours: number): string {
  try {
    const startDate = new Date(startISO);
    if (isNaN(startDate.getTime())) return startISO;
    const endDate = new Date(startDate.getTime() + (durationHours || 0) * 3600 * 1000);
    return formatISO(endDate);
  } catch (_e) {
    return startISO;
  }
}

function calculateDuration(startISO: string, endISO: string): number {
  try {
    const s = new Date(startISO).getTime();
    const e = new Date(endISO).getTime();
    if (!isNaN(s) && !isNaN(e) && e >= s) {
      const diffHours = (e - s) / (1000 * 60 * 60);
      return Math.round(diffHours * 100) / 100;
    }
  } catch (_e) {
    //
  }
  return 2;
}

export const EventModal: React.FC<EventModalProps> = ({
  isOpen,
  eventToEdit,
  onClose,
  onSave,
  onDelete,
  onDuplicate,
}) => {
  const [title, setTitle] = useState('');
  const [start, setStart] = useState('2026-09-28T10:00');
  const [end, setEnd] = useState('2026-09-28T12:00');
  const [durationHours, setDurationHours] = useState<number>(2);
  const [location, setLocation] = useState('');
  const [price, setPrice] = useState<number>(0);
  const [fixedCost, setFixedCost] = useState<number>(0);
  const [costPerPerson, setCostPerPerson] = useState<number>(0);
  const [capacity, setCapacity] = useState<number>(100);
  const [organisers, setOrganisers] = useState<number>(2);
  const [category, setCategory] = useState<Category>('Social');
  const [description, setDescription] = useState('');

  // Financial & Funding fields
  const [fundingModel, setFundingModel] = useState<FundingModel>('self-funded');
  const [stibetType, setStibetType] = useState<StibetType>('general');
  const [excursionDays, setExcursionDays] = useState<number>(1);
  const [nonFundableCost, setNonFundableCost] = useState<number>(0);
  const [externalFunding, setExternalFunding] = useState<number>(0);
  const [taxTier, setTaxTier] = useState<TaxTier>('none');

  useEffect(() => {
    if (eventToEdit) {
      setTitle(eventToEdit.title);
      setStart(eventToEdit.start);
      setEnd(eventToEdit.end);
      const dur = calculateDuration(eventToEdit.start, eventToEdit.end);
      setDurationHours(dur);
      setLocation(eventToEdit.location);
      setPrice(eventToEdit.price);
      setFixedCost(eventToEdit.fixedCost);
      setCostPerPerson(eventToEdit.costPerPerson);
      setCapacity(eventToEdit.capacity);
      setOrganisers(eventToEdit.organisers);
      setCategory(eventToEdit.category);
      setDescription(eventToEdit.description || '');

      setFundingModel(eventToEdit.fundingModel || (eventToEdit.price > 0 ? 'stibet-partial' : 'fully-free'));
      setStibetType(eventToEdit.stibetType || (eventToEdit.category === 'Trip' ? 'one-day-excursion' : 'general'));
      setExcursionDays(eventToEdit.excursionDays || 1);
      setNonFundableCost(eventToEdit.nonFundableCost || 0);
      setExternalFunding(eventToEdit.externalFunding || 0);
      setTaxTier(eventToEdit.taxTier || (eventToEdit.category === 'Drinking' ? 'alcohol' : eventToEdit.category === 'Culture' ? 'cultural' : 'none'));
    } else {
      // Defaults for new event
      const initialStart = '2026-09-28T10:00';
      const initialDuration = 2;
      const initialEnd = calculateEnd(initialStart, initialDuration);

      setTitle('');
      setStart(initialStart);
      setEnd(initialEnd);
      setDurationHours(initialDuration);
      setLocation('Campus Main Hall');
      setPrice(0);
      setFixedCost(100);
      setCostPerPerson(2);
      setCapacity(100);
      setOrganisers(3);
      setCategory('Social');
      setDescription('');

      setFundingModel('stibet-free');
      setStibetType('general');
      setExcursionDays(1);
      setNonFundableCost(0);
      setExternalFunding(0);
      setTaxTier('none');
    }
  }, [eventToEdit, isOpen]);

  if (!isOpen) return null;

  // Handle start time change -> automatically adjust end date based on duration
  const handleStartChange = (newStartISO: string) => {
    setStart(newStartISO);
    const newEndISO = calculateEnd(newStartISO, durationHours);
    setEnd(newEndISO);
  };

  // Handle duration change -> dynamically recalculate end date & time
  const handleDurationChange = (newDuration: number) => {
    const val = Math.max(0.25, newDuration);
    setDurationHours(val);
    const newEndISO = calculateEnd(start, val);
    setEnd(newEndISO);
  };

  // Handle manual end date change -> update durationHours state
  const handleEndChange = (newEndISO: string) => {
    setEnd(newEndISO);
    const dur = calculateDuration(start, newEndISO);
    setDurationHours(dur);
  };

  // Draft event object for live preview calculations
  const draftEvent: EventItem = {
    id: eventToEdit ? eventToEdit.id : 'preview',
    title: title || 'Draft Event',
    start,
    end,
    location: location || 'TBD',
    price: Number(price) || 0,
    fixedCost: Number(fixedCost) || 0,
    costPerPerson: Number(costPerPerson) || 0,
    capacity: Number(capacity) || 1,
    organisers: Number(organisers) || 1,
    category,
    fundingModel,
    stibetType,
    excursionDays,
    nonFundableCost,
    externalFunding,
    taxTier,
    description,
  };

  const financialDetails = calculateEventFinancials(draftEvent);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) return;

    const newEvent: EventItem = {
      id: eventToEdit ? eventToEdit.id : `ev-${Date.now()}`,
      title: title.trim(),
      start,
      end,
      location: location.trim() || 'TBD',
      price: Number(price) || 0,
      fixedCost: Number(fixedCost) || 0,
      costPerPerson: Number(costPerPerson) || 0,
      capacity: Number(capacity) || 1,
      organisers: Number(organisers) || 1,
      category,
      description: description.trim(),
      fundingModel,
      stibetType,
      excursionDays,
      nonFundableCost,
      externalFunding,
      taxTier,
    };

    onSave(newEvent);
  };

  const handleDuplicate = () => {
    if (!onDuplicate) return;

    const currentTitle = title.trim() || (eventToEdit ? eventToEdit.title : 'Untitled Event');
    const duplicateTitle = currentTitle.endsWith('(Copy)')
      ? currentTitle
      : `${currentTitle} (Copy)`;

    const duplicatedEvent: EventItem = {
      id: `ev-${Date.now()}`,
      title: duplicateTitle,
      start,
      end,
      location: location.trim() || 'TBD',
      price: Number(price) || 0,
      fixedCost: Number(fixedCost) || 0,
      costPerPerson: Number(costPerPerson) || 0,
      capacity: Number(capacity) || 1,
      organisers: Number(organisers) || 1,
      category,
      description: description.trim(),
      fundingModel,
      stibetType,
      excursionDays,
      nonFundableCost,
      externalFunding,
      taxTier,
    };

    onClose();
    onDuplicate(duplicatedEvent);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" style={{ maxWidth: '780px' }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">
            {eventToEdit ? 'Edit Event & Funding Model' : 'Create New Event'}
          </h2>
          <button className="icon-btn" onClick={onClose} title="Close">
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="form-group full">
              <label>Event Title *</label>
              <input
                type="text"
                className="form-control"
                value={title}
                onChange={e => setTitle(e.target.value)}
                placeholder="e.g. City Center Campus Rally"
                required
              />
            </div>

            <div className="form-grid">
              <div className="form-group">
                <label>Category</label>
                <select
                  className="form-control"
                  value={category}
                  onChange={e => setCategory(e.target.value as Category)}
                >
                  {CATEGORIES.map(cat => (
                    <option key={cat} value={cat}>
                      {cat}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Location</label>
                <input
                  type="text"
                  className="form-control"
                  value={location}
                  onChange={e => setLocation(e.target.value)}
                  placeholder="e.g. Student Center"
                />
              </div>

              <div className="form-group">
                <label>Start Date & Time *</label>
                <input
                  type="datetime-local"
                  className="form-control"
                  value={start}
                  onChange={e => handleStartChange(e.target.value)}
                  required
                />
              </div>

              <div className="form-group">
                <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span>Duration (Hours)</span>
                  <span style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>Auto End</span>
                </label>
                <input
                  type="number"
                  min="0.25"
                  max="48"
                  step="0.25"
                  className="form-control"
                  value={durationHours}
                  onChange={e => handleDurationChange(parseFloat(e.target.value) || 0.25)}
                />
              </div>

              <div className="form-group full">
                <label>End Date & Time *</label>
                <input
                  type="datetime-local"
                  className="form-control"
                  value={end}
                  onChange={e => handleEndChange(e.target.value)}
                  required
                />
              </div>
            </div>

            {/* Funding Model & STIBET Rules Section */}
            <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid var(--border)' }}>
              <h3 style={{ fontSize: '0.95rem', fontWeight: 700, marginBottom: '0.75rem', color: 'var(--text-primary)' }}>
                Funding Model &amp; DAAD STIBET Settings
              </h3>

              <div className="form-grid">
                <div className="form-group">
                  <label>Funding Structure</label>
                  <select
                    className="form-control"
                    value={fundingModel}
                    onChange={e => setFundingModel(e.target.value as FundingModel)}
                  >
                    <option value="stibet-free">STIBET Free (Free for participants, paid by STIBET)</option>
                    <option value="stibet-partial">STIBET Partial (Mixed: Ticket fee + STIBET grant)</option>
                    <option value="fully-free">Fully Free (Section-funded / €0 cost to all)</option>
                    <option value="external">External (Externally hosted / paid by partner)</option>
                    <option value="self-funded">Self-Funded (100% participant ticket fee)</option>
                  </select>
                </div>

                {(fundingModel === 'stibet-free' || fundingModel === 'stibet-partial') && (
                  <div className="form-group">
                    <label>STIBET Funding Category</label>
                    <select
                      className="form-control"
                      value={stibetType}
                      onChange={e => setStibetType(e.target.value as StibetType)}
                    >
                      <option value="general">General Event (Up to €20/participant)</option>
                      <option value="one-day-excursion">One-Day Excursion (Up to €20/head, max 75%)</option>
                      <option value="multi-day-excursion">Multi-Day Excursion (Up to €30/head/day, max 75%)</option>
                    </select>
                  </div>
                )}

                {stibetType === 'multi-day-excursion' && (fundingModel === 'stibet-free' || fundingModel === 'stibet-partial') && (
                  <div className="form-group">
                    <label>Excursion Duration (Days)</label>
                    <input
                      type="number"
                      min="1"
                      max="14"
                      className="form-control"
                      value={excursionDays}
                      onChange={e => setExcursionDays(parseInt(e.target.value, 10) || 1)}
                    />
                  </div>
                )}

                <div className="form-group">
                  <label>Tax Tier (Umsatzsteuer)</label>
                  <select
                    className="form-control"
                    value={taxTier}
                    onChange={e => setTaxTier(e.target.value as TaxTier)}
                  >
                    <option value="none">0% Tax Exempt / General</option>
                    <option value="cultural">7% Cultural Event (Reduced tax rate)</option>
                    <option value="alcohol">19% Party / Alcohol Event (Standard tax rate)</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Non-Fundable Costs (€) (Alcohol/Pfand/Running)</label>
                  <input
                    type="number"
                    min="0"
                    step="5"
                    className="form-control"
                    value={nonFundableCost}
                    onChange={e => setNonFundableCost(parseFloat(e.target.value) || 0)}
                  />
                </div>

                {fundingModel === 'external' && (
                  <div className="form-group">
                    <label>External Sponsor Funding (€)</label>
                    <input
                      type="number"
                      min="0"
                      step="10"
                      className="form-control"
                      value={externalFunding}
                      onChange={e => setExternalFunding(parseFloat(e.target.value) || 0)}
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Capacity & Pricing Controls */}
            <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid var(--border)' }}>
              <h3 style={{ fontSize: '0.95rem', fontWeight: 700, marginBottom: '0.75rem', color: 'var(--text-primary)' }}>
                Expenses, Capacity &amp; Ticket Pricing
              </h3>

              <div className="form-grid">
                <div className="form-group">
                  <label>Total Fixed Cost (€)</label>
                  <input
                    type="number"
                    min="0"
                    step="5"
                    className="form-control"
                    value={fixedCost}
                    onChange={e => setFixedCost(parseFloat(e.target.value) || 0)}
                  />
                </div>

                <div className="form-group">
                  <label>Variable Cost / Person (€)</label>
                  <input
                    type="number"
                    min="0"
                    step="0.5"
                    className="form-control"
                    value={costPerPerson}
                    onChange={e => setCostPerPerson(parseFloat(e.target.value) || 0)}
                  />
                </div>

                <div className="form-group">
                  <label>Max Capacity (Spots)</label>
                  <input
                    type="number"
                    min="1"
                    className="form-control"
                    value={capacity}
                    onChange={e => setCapacity(parseInt(e.target.value, 10) || 1)}
                  />
                </div>

                <div className="form-group">
                  <label>Organisers Assigned</label>
                  <input
                    type="number"
                    min="1"
                    className="form-control"
                    value={organisers}
                    onChange={e => setOrganisers(parseInt(e.target.value, 10) || 1)}
                  />
                </div>

                <div className="form-group full">
                  <label style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span>Participant Ticket Price (€)</span>
                    <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                      Min Recommended: €{financialDetails.recommendedMinPricePerParticipant.toFixed(2)}
                    </span>
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.5"
                    className="form-control"
                    value={price}
                    onChange={e => setPrice(parseFloat(e.target.value) || 0)}
                    disabled={fundingModel === 'fully-free' || fundingModel === 'stibet-free'}
                  />
                </div>
              </div>
            </div>

            <div className="form-group full" style={{ marginTop: '1rem' }}>
              <label>Description &amp; Notes</label>
              <textarea
                rows={2}
                className="form-control"
                value={description}
                onChange={e => setDescription(e.target.value)}
                placeholder="Additional info, equipment requirements, contact persons..."
              />
            </div>

            {/* Dynamic Financial Preview Box */}
            <div
              style={{
                marginTop: '1rem',
                background: 'var(--bg-app)',
                border: '1px solid var(--border)',
                borderRadius: 'var(--radius-md)',
                padding: '1rem 1.25rem',
                fontSize: '0.85rem',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem', borderBottom: '1px solid var(--border-subtle)', pb: '0.4rem' }}>
                <strong style={{ color: 'var(--text-primary)' }}>Live Financial &amp; STIBET Calculation</strong>
                <span className="brand-badge" style={{ background: 'var(--accent-primary)', fontSize: '0.7rem' }}>
                  {fundingModel.toUpperCase()}
                </span>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.75rem', marginTop: '0.5rem' }}>
                <div>
                  <span style={{ color: 'var(--text-muted)', display: 'block' }}>Total Event Cost:</span>
                  <strong style={{ color: 'var(--text-primary)', fontSize: '1rem' }}>€{financialDetails.totalCost.toFixed(2)}</strong>
                </div>

                <div>
                  <span style={{ color: 'var(--text-muted)', display: 'block' }}>STIBET Grant (w/ 10% buffer):</span>
                  <strong style={{ color: '#10b981', fontSize: '1rem' }}>€{financialDetails.appliedStibetFunding.toFixed(2)}</strong>
                </div>

                <div>
                  <span style={{ color: 'var(--text-muted)', display: 'block' }}>Net Cost to Recover:</span>
                  <strong style={{ color: 'var(--text-primary)', fontSize: '1rem' }}>€{financialDetails.netCostToRecover.toFixed(2)}</strong>
                </div>

                <div>
                  <span style={{ color: 'var(--text-muted)', display: 'block' }}>Min Participant Fee:</span>
                  <strong style={{ color: '#38bdf8', fontSize: '1rem' }}>€{financialDetails.recommendedMinPricePerParticipant.toFixed(2)}</strong>
                </div>

                <div>
                  <span style={{ color: 'var(--text-muted)', display: 'block' }}>Gross Ticket Sales:</span>
                  <strong style={{ color: 'var(--text-primary)', fontSize: '1rem' }}>€{financialDetails.projectedParticipantRevenue.toFixed(2)}</strong>
                </div>

                <div>
                  <span style={{ color: 'var(--text-muted)', display: 'block' }}>Section Net Balance:</span>
                  <strong style={{ color: financialDetails.sectionNetBalance >= 0 ? '#10b981' : '#ef4444', fontSize: '1rem' }}>
                    {financialDetails.sectionNetBalance >= 0 ? `+€${financialDetails.sectionNetBalance.toFixed(2)}` : `-€${Math.abs(financialDetails.sectionNetBalance).toFixed(2)}`}
                  </strong>
                </div>
              </div>
            </div>
          </div>

          <div className="modal-footer">
            <div style={{ marginRight: 'auto', display: 'flex', gap: '0.75rem' }}>
              {eventToEdit && eventToEdit.id && onDelete && (
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={() => {
                    onClose();
                    onDelete(eventToEdit.id);
                  }}
                >
                  <Trash2 size={16} />
                  <span>Delete Event</span>
                </button>
              )}
              {eventToEdit && onDuplicate && (
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleDuplicate}
                >
                  <Copy size={16} />
                  <span>Duplicate Event</span>
                </button>
              )}
            </div>
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary">
              <Save size={16} />
              <span>Save Event</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

